import {
  AccountType,
  InvestorCustomTagCategory,
  InvestorType,
} from 'further-types/investor';
import {
  validateUkSortCode,
  validateUserName,
  isValidEmail,
} from 'further-ui/utils';
import { Country } from 'further-ui/labels';
import { z } from 'zod';
import { ApiResponses } from 'lib/trpcClient';

type GetInvestorResponse = ApiResponses['investor']['getInvestor'];

const createEmailValidation = (existingEmail?: string) => {
  return z
    .string()
    .min(1, 'Please enter email.')
    .refine(
      (value) => {
        // If email hasn't changed, skip format validation
        if (existingEmail === value) {
          return true;
        }
        // If email has changed, validate format
        return isValidEmail(value);
      },
      { message: 'Invalid email address.' },
    );
};

const commonInvestorRules = {
  firmId: z.string(),
  email: z
    .string()
    .min(1, 'Please enter email.')
    .email('Invalid email address.'),
  externalInvestorId: z.string().optional(),
  investorType: z.nativeEnum(InvestorType).optional(),
  isClaimingTax: z.boolean(),
  dateRegister: z.date().optional().nullable(),
  address: z.array(
    z.object({
      address1: z.string().min(1, 'Please enter address line 1.'),
      address2: z.string().optional(),
      city: z.string().min(1, 'Please enter city/town.'),
      postcode: z.string().min(1, 'Please enter postal or zip code.'),
      country: z
        .nativeEnum(Country)
        .or(z.literal(''))
        .refine((value): boolean => value !== '', {
          message: 'Please select country.',
        }),
      isApplyAddress: z.boolean().optional(),
    }),
    { required_error: 'Please provide at least one address.' },
  ),
  isVulnerableClient: z.boolean(),
  vulnerabilityNotes: z.string().optional(),
  isDeceased: z.boolean(),
  requiresHardCopyReports: z.boolean(),
  isUkRemittanceTaxpayer: z.boolean(),
  customTags: z
    .array(
      z.object({
        category: z.nativeEnum(InvestorCustomTagCategory),
        tag: z.string(),
      }),
    )
    .nullish(),
  dateOfDeath: z.date().nullable().optional(),
  defaultAdviserId: z.string().nullable().optional(),
};

const bankAndTaxRules = {
  bankAccountName: z.string().optional(),
  bankAccountNumber: z.string().optional(),
  bankName: z.string().optional(),
  bankSortCode: z.string().optional(),
  iban: z.string().optional(),
  internationalBankCode: z.string().optional(),
  swift: z.string().optional(),
  isUsPerson: z.boolean().optional(),
  taxResidencies: z.array(
    z.object({
      country: z.string(),
    }),
  ),
};

const nameSuperRefine = (value?: string, context?: z.RefinementCtx) => {
  if (!value) return;
  const nameError = validateUserName(value);
  if (nameError) {
    context?.addIssue({
      code: z.ZodIssueCode.custom,
      message: nameError,
    });
  }
};

const individualInvestorOptionalFields = {
  profession: z.string().optional(),
  sector: z.string().optional(),
  primaryTaxId: z.string().optional(),
  secondaryTaxId: z.string().optional(),
  incomeSources: z
    .array(
      z.object({
        value: z.string(),
      }),
    )
    .optional(),
};

const individualInvestorSchema = z.object({
  accountType: z.literal(AccountType.Individual),
  ...commonInvestorRules,
  ...bankAndTaxRules,
  ...individualInvestorOptionalFields,
  title: z.string().nullish(),
  firstName: z
    .string()
    .min(1, 'Please enter first name.')
    .superRefine(nameSuperRefine),
  middleName: z.string().superRefine(nameSuperRefine),
  lastName: z
    .string()
    .min(1, 'Please enter surname.')
    .superRefine(nameSuperRefine),
  phoneNumber: z.string().min(1, 'Please enter phone number.'),
  dob: z.date({
    required_error: 'Please enter date of birth.',
    invalid_type_error: 'Please enter date of birth.',
  }),
  nationality: z
    .string({
      required_error: 'Please select nationality.',
      invalid_type_error: 'Please select nationality.',
    })
    .min(1, 'Please select nationality.'),
  otherNationality: z
    .array(
      z.object({
        country: z.string().min(1, 'Please select other nationality.'),
      }),
    )
    .optional(),
});

const legalEntitySchema = z.object({
  accountType: z.literal(AccountType.LegalEntity),
  ...bankAndTaxRules,
  ...commonInvestorRules,
  registeredName: z.string().min(1, 'Please enter registered name.'),
  registrationNumber: z.string().min(1, 'Please enter registration number.'),
  registrationDate: z.date({
    required_error: 'Please enter date of birth.',
  }),
  registrationState: z.string().min(1, 'Please enter registration state.'),
  registeredTaxNumber: z.string().optional(),
  keyContactFirstName: z.string().min(1, 'Please enterkey contact first name.'),
  keyContactSurname: z.string().min(1, 'Please enter key contact surname.'),
  keyContactPhoneNumber: z
    .string()
    .min(1, 'Please enter key contact phone number.'),
});

export const investorSchema = z
  .discriminatedUnion('accountType', [
    individualInvestorSchema,
    legalEntitySchema,
  ])
  .superRefine((values, context) => {
    if (values.bankAccountNumber && values.bankAccountNumber.length !== 8) {
      context.addIssue({
        path: ['bankAccountNumber'],
        code: z.ZodIssueCode.custom,
        message: 'Please enter an 8 digit account number.',
      });
    }
    if (values.bankSortCode && !validateUkSortCode(values.bankSortCode)) {
      context.addIssue({
        path: ['bankSortCode'],
        code: z.ZodIssueCode.custom,
        message: 'Please enter a 6 digit sort code.',
      });
    }

    if (values.isDeceased && !values.dateOfDeath) {
      context.addIssue({
        path: ['dateOfDeath'],
        code: z.ZodIssueCode.custom,
        message: 'Please provide a date of death.',
      });
    }
  });

export type InvestorFormValues = Required<z.infer<typeof investorSchema>>;

export const createConditionalInvestorSchema = (
  isEdit: boolean,
  existingInvestor?: GetInvestorResponse,
) => {
  const checkIfStringPreviouslyPopulated =
    (existingValue: string | null | undefined) =>
    (currentValue: string | null | undefined) => {
      if (!isEdit) return currentValue && currentValue.length > 0;
      const wasPreviouslyPopulated =
        existingValue && existingValue.trim() !== '';
      return (
        !wasPreviouslyPopulated || (currentValue && currentValue.length > 0)
      );
    };

  const checkIfDatePreviouslyPopulated =
    (existingValue: Date | null | undefined) =>
    (currentValue: Date | null | undefined) => {
      if (!isEdit) return currentValue !== null;
      const wasPreviouslyPopulated =
        existingValue !== null && existingValue !== undefined;
      return !wasPreviouslyPopulated || currentValue !== null;
    };

  const conditionalCommonRules = {
    ...commonInvestorRules,
    email: createEmailValidation(existingInvestor?.email),
    address: z.array(
      z.object({
        address1: z
          .string()
          .refine(
            checkIfStringPreviouslyPopulated(
              existingInvestor?.address?.[0]?.address1,
            ),
            { message: 'Please enter address line 1.' },
          ),
        address2: z.string().optional(),
        city: z
          .string()
          .refine(
            checkIfStringPreviouslyPopulated(
              existingInvestor?.address?.[0]?.city,
            ),
            { message: 'Please enter city/town.' },
          ),
        postcode: z
          .string()
          .refine(
            checkIfStringPreviouslyPopulated(
              existingInvestor?.address?.[0]?.postcode,
            ),
            { message: 'Please enter postal or zip code.' },
          ),
        country: z
          .string()
          .refine(
            checkIfStringPreviouslyPopulated(
              existingInvestor?.address?.[0]?.country,
            ),
            { message: 'Please select country.' },
          ),
        isApplyAddress: z.boolean().optional(),
      }),
      { required_error: 'Please provide at least one address.' },
    ),
  };

  const conditionalIndividualSchema = z.object({
    accountType: z.literal(AccountType.Individual),
    ...conditionalCommonRules,
    ...bankAndTaxRules,
    ...individualInvestorOptionalFields,
    title: z.string().nullish(),
    firstName: z
      .string()
      .refine(checkIfStringPreviouslyPopulated(existingInvestor?.firstName), {
        message: 'Please enter first name.',
      })
      .superRefine(nameSuperRefine),
    middleName: z.string().superRefine(nameSuperRefine),
    lastName: z
      .string()
      .refine(checkIfStringPreviouslyPopulated(existingInvestor?.lastName), {
        message: 'Please enter surname.',
      })
      .superRefine(nameSuperRefine),
    phoneNumber: z
      .string()
      .refine(checkIfStringPreviouslyPopulated(existingInvestor?.phoneNumber), {
        message: 'Please enter phone number.',
      }),
    dob: z
      .date({
        required_error: 'Please enter date of birth.',
        invalid_type_error: 'Please enter date of birth.',
      })
      .nullable()
      .optional()
      .refine(checkIfDatePreviouslyPopulated(existingInvestor?.dob), {
        message: 'Please enter date of birth.',
      }),
    nationality: z
      .string({
        required_error: 'Please select nationality.',
        invalid_type_error: 'Please select nationality.',
      })
      .nullable()
      .optional()
      .refine(checkIfStringPreviouslyPopulated(existingInvestor?.nationality), {
        message: 'Please select nationality.',
      }),
    otherNationality: z
      .array(
        z.object({
          country: z.string().min(1, 'Please select other nationality.'),
        }),
      )
      .optional(),
  });

  const conditionalLegalEntitySchema = z.object({
    accountType: z.literal(AccountType.LegalEntity),
    ...conditionalCommonRules,
    ...bankAndTaxRules,
    registeredName: z
      .string()
      .refine(
        checkIfStringPreviouslyPopulated(existingInvestor?.registeredName),
        {
          message: 'Please enter registered name.',
        },
      ),
    registrationNumber: z
      .string()
      .refine(
        checkIfStringPreviouslyPopulated(existingInvestor?.registrationNumber),
        { message: 'Please enter registration number.' },
      ),
    registrationDate: z
      .date({
        required_error: 'Please enter registration date.',
      })
      .refine(
        checkIfDatePreviouslyPopulated(existingInvestor?.registrationDate),
        { message: 'Please enter registration date.' },
      ),
    registrationState: z
      .string()
      .refine(
        checkIfStringPreviouslyPopulated(existingInvestor?.registrationState),
        {
          message: 'Please enter registration state.',
        },
      ),
    registeredTaxNumber: z.string().optional(),
    keyContactFirstName: z
      .string()
      .refine(
        checkIfStringPreviouslyPopulated(existingInvestor?.keyContactFirstName),
        { message: 'Please enter key contact first name.' },
      ),
    keyContactSurname: z
      .string()
      .refine(
        checkIfStringPreviouslyPopulated(existingInvestor?.keyContactSurname),
        {
          message: 'Please enter key contact surname.',
        },
      ),
    keyContactPhoneNumber: z
      .string()
      .refine(
        checkIfStringPreviouslyPopulated(
          existingInvestor?.keyContactPhoneNumber,
        ),
        { message: 'Please enter key contact phone number.' },
      ),
  });

  return z
    .discriminatedUnion('accountType', [
      conditionalIndividualSchema,
      conditionalLegalEntitySchema,
    ])
    .superRefine((values, context) => {
      if (values.bankAccountNumber && values.bankAccountNumber.length !== 8) {
        context.addIssue({
          path: ['bankAccountNumber'],
          code: z.ZodIssueCode.custom,
          message: 'Please enter an 8 digit account number.',
        });
      }
      if (values.bankSortCode && !validateUkSortCode(values.bankSortCode)) {
        context.addIssue({
          path: ['bankSortCode'],
          code: z.ZodIssueCode.custom,
          message: 'Please enter a 6 digit sort code.',
        });
      }

      if (values.isDeceased && !values.dateOfDeath) {
        context.addIssue({
          path: ['dateOfDeath'],
          code: z.ZodIssueCode.custom,
          message: 'Please provide a date of death.',
        });
      }
    });
};

export const getInitialValues = (
  investorDetails: GetInvestorResponse,
  usersFirmId: string,
) => ({
  //Common values
  firmId: investorDetails?.firmId ?? usersFirmId ?? '',
  accountType:
    investorDetails?.accountType === AccountType.LegalEntity
      ? AccountType.LegalEntity
      : AccountType.Individual,
  email: investorDetails?.email ?? '',
  dateRegister: investorDetails?.dateRegister
    ? new Date(investorDetails.dateRegister)
    : null,
  investorType: investorDetails?.investorType,
  isClaimingTax: investorDetails?.isClaimingTax ?? true,
  externalInvestorId: investorDetails?.externalInvestorId ?? '',
  address: investorDetails?.address?.length
    ? investorDetails.address
    : [
        {
          isApplyAddress: true,
          address1: '',
          address2: '',
          city: '',
          postcode: '',
          country: '',
        },
      ],
  otherNationality: investorDetails?.otherNationality ?? [],
  taxResidencies: investorDetails?.taxResidencies?.length
    ? investorDetails.taxResidencies
    : [{ country: '' }],
  isVulnerableClient: !!investorDetails?.isVulnerableClient,
  vulnerabilityNotes: investorDetails?.vulnerabilityNotes ?? '',
  isDeceased: !!investorDetails?.isDeceased,
  requiresHardCopyReports: !!investorDetails?.requiresHardCopyReports,
  isUkRemittanceTaxpayer: !!investorDetails?.isUkRemittanceTaxpayer,
  customTags: investorDetails?.customTags,
  dateOfDeath: investorDetails?.dateOfDeath
    ? new Date(investorDetails.dateOfDeath)
    : null,

  //Individual investor
  title: investorDetails?.title ?? null,
  firstName: investorDetails?.firstName ?? '',
  middleName: investorDetails?.middleName ?? '',
  lastName: investorDetails?.lastName ?? '',
  phoneNumber: investorDetails?.phoneNumber ?? '',
  dob: investorDetails?.dob ? new Date(investorDetails.dob) : null,
  profession: investorDetails?.profession ?? '',
  sector: investorDetails?.sector ?? '',
  primaryTaxId: investorDetails?.primaryTaxId ?? '',
  secondaryTaxId: investorDetails?.secondaryTaxId ?? '',
  nationality: investorDetails?.nationality,
  incomeSources:
    investorDetails?.incomeSources?.map((value) => ({ value })) ?? [],

  //Legal entity
  registeredName: investorDetails?.registeredName ?? '',
  registrationNumber: investorDetails?.registrationNumber ?? '',
  registrationDate: investorDetails?.registrationDate
    ? new Date(investorDetails.registrationDate)
    : null,
  registrationState: investorDetails?.registrationState ?? '',
  registeredTaxNumber: investorDetails?.registeredTaxNumber ?? '',
  keyContactFirstName: investorDetails?.keyContactFirstName ?? '',
  keyContactSurname: investorDetails?.keyContactSurname ?? '',
  keyContactPhoneNumber: investorDetails?.keyContactPhoneNumber ?? '',
  defaultAdviserId: investorDetails?.defaultAdviserId ?? '',

  //Bank and tax details
  bankAccountName: investorDetails?.bankDetails?.bankAccountName ?? '',
  bankAccountNumber: investorDetails?.bankDetails?.bankAccountNumber ?? '',
  bankName: investorDetails?.bankDetails?.bankName ?? '',
  bankSortCode: investorDetails?.bankDetails?.bankSortCode ?? '',
  iban: investorDetails?.bankDetails?.iban ?? '',
  swift: investorDetails?.bankDetails?.swift ?? '',
  internationalBankCode:
    investorDetails?.bankDetails?.internationalBankCode ?? '',
  isUsPerson: !!investorDetails?.bankDetails?.isUsPerson,
});
